<template>
  <div class="relative" ref="root">
    <button
      v-if="!inline"
      type="button"
      class="uk-select text-left"
      style="min-width: 220px; max-width: 260px"
      @click="open = !open"
    >
      <span
        class="truncate block pr-4"
        :class="{ 'text-gray-400': !value.length }"
      >
        {{ triggerLabel }}
      </span>
    </button>

    <div
      v-if="open || inline"
      class="bg-white rounded-lg ring-1 ring-gray-200 text-sm"
      :class="{ 'absolute left-0 top-full mt-1 z-50 w-72 shadow-xl': !inline }"
    >
      <div class="p-2 border-b border-gray-100">
        <input
          ref="search"
          v-model="search"
          type="text"
          class="uk-input w-full"
          placeholder="Kişi ara..."
        />
      </div>

      <label
        v-if="visibleOptions.length"
        class="flex items-center justify-between px-3 py-2 border-b border-gray-100 cursor-pointer hover:bg-gray-50"
      >
        <span class="flex items-center space-x-2 font-medium">
          <input
            type="checkbox"
            class="uk-checkbox"
            :checked="allVisibleSelected"
            :indeterminate.prop="someVisibleSelected && !allVisibleSelected"
            @change="toggleAllVisible"
          />
          <span>Tümünü seç</span>
        </span>
        <span class="text-xs text-gray-400">{{ visibleOptions.length }}</span>
      </label>

      <div class="overflow-y-auto py-1" :class="inline ? 'max-h-56' : 'max-h-72'">
        <div
          v-if="!visibleOptions.length"
          class="px-3 py-4 text-center text-gray-400"
        >
          Kişi bulunamadı
        </div>
        <label
          v-for="option in visibleOptions"
          :key="option.id"
          class="flex items-center space-x-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer"
        >
          <input
            type="checkbox"
            class="uk-checkbox"
            :checked="selectedSet[option.id]"
            @change="toggle(option.id)"
          />
          <span class="truncate">{{ option.name }}</span>
        </label>
      </div>

      <div
        class="flex items-center justify-between px-3 py-2 border-t border-gray-100 bg-gray-50 rounded-b-lg"
      >
        <span class="text-xs text-gray-500">
          <template v-if="value.length">
            <strong class="text-gray-800">{{ value.length }}</strong> kişi
            <template v-if="perPerson">
              · kişi başı {{ perPerson }} kayıt</template
            >
          </template>
          <template v-else>Dağıtılacak kişileri seçin</template>
        </span>
        <button v-if="!inline" type="button" class="global-button-xs" @click="open = false">
          Tamam
        </button>
      </div>
    </div>
  </div>
</template>

<script>
// Toplu agent / retention atamasında kayıtların eşit dağıtılacağı kişileri seçtirir.
// options: alanın kendi seçenekleri ({ id, name }), value: seçili id'ler
// inline: açılır menü yerine liste olarak gösterilir (modal içinde kesilmesin)
export default {
  props: {
    value: { type: Array, default: () => [] },
    options: { type: Array, default: () => [] },
    // Dağıtılacak kayıt sayısı (kişi başı önizleme için)
    recordCount: { type: Number, default: 0 },
    inline: { type: Boolean, default: false },
  },
  data() {
    return { open: false, search: "" };
  },
  computed: {
    selectedSet() {
      const set = {};
      this.value.forEach((id) => (set[id] = true));
      return set;
    },
    visibleOptions() {
      const q = this.search.trim().toLocaleLowerCase("tr");
      if (!q) return this.options;
      return this.options.filter((o) =>
        String(o.name || "")
          .toLocaleLowerCase("tr")
          .includes(q)
      );
    },
    allVisibleSelected() {
      return (
        this.visibleOptions.length > 0 &&
        this.visibleOptions.every((o) => this.selectedSet[o.id])
      );
    },
    someVisibleSelected() {
      return this.visibleOptions.some((o) => this.selectedSet[o.id]);
    },
    triggerLabel() {
      if (!this.value.length) return "Kişileri seçin";
      if (this.value.length <= 2) {
        return this.options
          .filter((o) => this.selectedSet[o.id])
          .map((o) => o.name)
          .join(", ");
      }
      return `${this.value.length} kişi arasında eşit dağıt`;
    },
    perPerson() {
      if (!this.recordCount || !this.value.length) return null;
      const min = Math.floor(this.recordCount / this.value.length);
      const max = Math.ceil(this.recordCount / this.value.length);
      return min === max ? `${min}` : `${min}–${max}`;
    },
  },
  watch: {
    open(isOpen) {
      if (isOpen) {
        document.addEventListener("mousedown", this.onOutside);
        this.$nextTick(() => this.$refs.search && this.$refs.search.focus());
      } else {
        document.removeEventListener("mousedown", this.onOutside);
        this.search = "";
      }
    },
  },
  beforeDestroy() {
    document.removeEventListener("mousedown", this.onOutside);
  },
  methods: {
    onOutside(e) {
      if (this.$refs.root && !this.$refs.root.contains(e.target))
        this.open = false;
    },
    toggle(id) {
      this.$emit(
        "input",
        this.selectedSet[id]
          ? this.value.filter((v) => v !== id)
          : [...this.value, id]
      );
    },
    // Arama varsa sadece görünen kişileri seçer / bırakır
    toggleAllVisible() {
      const ids = this.visibleOptions.map((o) => o.id);
      if (this.allVisibleSelected) {
        this.$emit(
          "input",
          this.value.filter((v) => !ids.includes(v))
        );
      } else {
        this.$emit("input", [...new Set([...this.value, ...ids])]);
      }
    },
  },
};
</script>
